import FilterContainer, { FilterProps } from '../filter-container';
import { useTranslation } from 'react-i18next';
import {
  FlexColumn,
  FlexRow,
  Input,
  InputValidationType,
} from '@make-software/cspr-design';
import React, { useEffect, useState } from 'react';
import { BodyText } from '@block-explorer/ui';
import { useFilterQuery } from '../filter-query';

interface RangeFilterProps extends FilterProps {
  fromFilterId: string;
  toFilterId: string;
  label: string;
  handleValidateInputs: (
    state: Record<'from' | 'to', string>
  ) => Record<'from' | 'to', string | null>;
}

const MAX_ERROR_MESSAGE_LENGTH = 20;

export const RangeFilter = ({
  fromFilterId,
  toFilterId,
  label,
  handleValidateInputs,
  filters,
  setFilters,
}: RangeFilterProps) => {
  const { t } = useTranslation();
  const { queryFilters, syncFiltersToQuery } = useFilterQuery();

  const queryFromValue = queryFilters?.[fromFilterId];
  const queryToValue = queryFilters?.[toFilterId];

  const [state, setState] = useState<Record<'from' | 'to', string>>({
    from: filters?.[fromFilterId] || queryFromValue || '',
    to: filters?.[toFilterId] || queryToValue || '',
  });
  const [errors, setErrors] = useState<Record<'from' | 'to', string | null>>({
    from: null,
    to: null,
  });

  useEffect(() => {
    if (!queryFromValue && !queryToValue) return;

    const nextFromValue = queryFromValue || '';
    const nextToValue = queryToValue || '';

    if (state.from !== nextFromValue || state.to !== nextToValue) {
      setState({
        from: nextFromValue,
        to: nextToValue,
      });
      setErrors({
        from: null,
        to: null,
      });
    }

    if (queryFromValue && filters?.[fromFilterId] !== queryFromValue) {
      const newFilters = { ...(filters || {}), [fromFilterId]: queryFromValue };
      setFilters(newFilters);
    }

    if (queryToValue && filters?.[toFilterId] !== queryToValue) {
      const newFilters = { ...(filters || {}), [toFilterId]: queryToValue };
      setFilters(newFilters);
    }
  }, [queryFromValue, queryToValue, fromFilterId, toFilterId]);

  const handleChangeInputValue = (id, text) => {
    setErrors({
      ...errors,
      [id]: null,
    });
    setState({
      ...state,
      [id]: text,
    });
  };

  const handleSetFilters = () => {
    const errorMap = handleValidateInputs(state);
    const hasErrors = !!errorMap.from || !!errorMap.to;
    if (!hasErrors) {
      const newFilters = {
        ...filters,
        [fromFilterId]: state.from,
        [toFilterId]: state.to,
      };
      setFilters(newFilters);
      syncFiltersToQuery(newFilters);
    } else {
      setErrors(errorMap);
    }
  };
  const handleResetFilters = () => {
    setState({
      from: '',
      to: '',
    });
    setErrors({
      from: null,
      to: null,
    });

    const { [fromFilterId]: _, [toFilterId]: __, ...rest } = filters || {};
    setFilters(rest);
    syncFiltersToQuery(rest);
  };

  const isFilterSet = Object.keys(filters || {})?.some(
    (filter) => filter.includes(fromFilterId) || filter.includes(toFilterId)
  );
  const hasErrors = !!errors.from || !!errors.to;
  const disabled = (!state.from && !state.to) || hasErrors;

  const responsiveErrorsMargin =
    errors.from?.length > MAX_ERROR_MESSAGE_LENGTH ||
    errors.to?.length > MAX_ERROR_MESSAGE_LENGTH
      ? 28
      : hasErrors
        ? 12
        : 0;

  return (
    <FilterContainer
      isFilterSet={isFilterSet}
      onSetFilters={handleSetFilters}
      onResetFilters={handleResetFilters}
      onResetErrors={() => setErrors({ from: null, to: null })}
      disabled={disabled}
    >
      <FlexColumn
        gap={4}
        style={{
          marginBottom: responsiveErrorsMargin,
          paddingBottom: 8,
        }}
      >
        <BodyText
          size={1}
          scale={'sm'}
          variation={'black'}
          aria-label={`${fromFilterId}-range-filter`}
          id={`${fromFilterId}-range-filter`}
          tabIndex={0}
        >
          {label}
        </BodyText>
        <FlexRow align={'center'} justify={'space-between'}>
          <Input
            id={`${fromFilterId}-range-filter`}
            style={{ flex: 1 }}
            onChange={(event) => {
              handleChangeInputValue('from', event.target.value);
            }}
            placeholder={t('From')}
            value={state.from}
            validationType={InputValidationType.PositiveInteger}
            error={!!errors.from}
            validationText={errors.from}
            autoFocus
          />
          <BodyText
            size={1}
            style={{ margin: '0 7px' }}
            variation={'gray'}
            scale={'sm'}
          >
            {' - '}
          </BodyText>

          <Input
            id={`${toFilterId}-input-filter`}
            style={{ flex: 1 }}
            onChange={(event) => {
              handleChangeInputValue('to', event.target.value);
            }}
            placeholder={t('To')}
            value={state.to}
            validationType={InputValidationType.PositiveInteger}
            error={!!errors.to}
            validationText={errors.to}
          />
        </FlexRow>
      </FlexColumn>
    </FilterContainer>
  );
};
